import { useState } from 'react';
import axios from 'axios';

export default function ImportModal({ title, endpoint, columns, onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    setResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(endpoint, formData, {
        headers: {
          ...headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      setResult(res.data);
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
      <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 480 }}>
        
        <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>
          Import {title}
        </h3>
        <p style={{ color: "#cbd5e1", fontSize: 14.5, margin: "0 0 24px" }}>
          Upload an Excel or CSV file exported from the external system
        </p>

        {/* Expected columns */}
        <div style={{ background: "rgba(56,189,248,0.05)", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
          <div style={{ color: "#cbd5e1", fontSize: 12.5, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>
            Expected Columns
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {columns.map(col => (
              <span key={col} style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.2)", borderRadius: 6, padding: "3px 10px", color: "#38bdf8", fontSize: 13.5 }}>
                {col}
              </span>
            ))}
          </div>
        </div>

        {/* File input */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>
            Select File
          </label>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={e => setFile(e.target.files[0])}
            style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "10px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", cursor: "pointer" }}
          />
          {file && (
            <div style={{ color: "#10b981", fontSize: 13.5, marginTop: 6 }}>
              {file.name} ({(file.size / 1024).toFixed(1)} KB)
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 14.5, marginBottom: 16 }}>
            {error}
          </div>
        )}

        {/* Result */}
        {result && (
          <div style={{ background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.2)", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
            <div style={{ color: "#10b981", fontWeight: 700, fontSize: 15, marginBottom: 8 }}>
              Import Complete
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[
                ['Total Rows', result.total],
                ['Created', result.created],
                ['Updated', result.updated]
              ].map(([k, v]) => (
                <div key={k} style={{ background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "8px 12px", textAlign: "center" }}>
                  <div style={{ color: "#cbd5e1", fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>{k}</div>
                  <div style={{ color: "#10b981", fontWeight: 700, fontSize: 18 }}>{v}</div>
                </div>
              ))}
            </div>
            {result.errors?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ color: "#fbbf24", fontSize: 13.5, marginBottom: 4 }}>{result.errors.length} rows skipped:</div>
                {result.errors.slice(0, 3).map((e, i) => (
                  <div key={i} style={{ color: "#cbd5e1", fontSize: 12.5, marginBottom: 2 }}>• {e}</div>
                ))}
                {result.errors.length > 3 && (
                  <div style={{ color: "#cbd5e1", fontSize: 12.5 }}>...and {result.errors.length - 3} more</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}
          >
            {result ? 'Close' : 'Cancel'}
          </button>
          {!result && (
            <button
              onClick={handleUpload}
              disabled={!file || loading}
              style={{ flex: 1, background: file && !loading ? "linear-gradient(135deg, #0891b2, #0e7490)" : "rgba(8,145,178,0.3)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: file && !loading ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
            >
              {loading ? (
                <>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTop: "2px solid #fff", animation: "spin 1s linear infinite" }} />
                  Importing...
                </>
              ) : 'Import'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}